import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Copy, Check, RefreshCw, ShieldCheck } from 'lucide-react';
import { Container, PageHeader, ActionBar } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  IconButton,
  Badge,
  Stepper,
  Input,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  RadioGroup,
  RadioGroupItem,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useQuickSetupSessions,
  useCreateQuickSetup,
  useUpdateQuickSetup,
  useExecuteQuickSetup,
  useCertinventUpload,
  useWaapAspect,
  useDistributeTigerCertToWaap,
  WAAP_AREAS,
  type QuickSetupConfig,
  type QuickSetupRecord,
  type WaapWebsiteInfo,
} from '@/api/waap';
import {
  useIssueCert,
  useRecheckCert,
  useCertDetail,
  useCerts,
  useActivateWorkspace,
  type CertStatus,
} from '@/api/tiger';
import { useServiceWaapDomainsPaged } from '@/api/manticore/service-waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { wafPolicyById } from '@/pages/services/waap/waf-policies';
import { errMessage, certDnsNamesFor } from '@/pages/services/waap/waap-util';
import { Labeled } from './settings/settings-ui';
import {
  trackOnboardingStep,
  trackOnboardingComplete,
  trackOnboardingFailed,
  trackDomainAdd,
  trackWafEnable,
} from '@/lib/analytics-events';
import styles from './QuickSetupTab.module.css';
import { brand } from '@/config/brand';

// The WAAP solution id the wizard is scoped to. The vendor quick-setup uses 401
// for the SCDN/WAAP solution (verified against the flashcone console network
// flow); an incorrect id makes /quick_setup return 500. TODO: resolve from the
// service binding if this ever varies per account.
const SOLUTION_ID = 401;

const STEP_LABELS = ['Resume/Restart', 'Domain & Origin', 'SSL', 'Security Features'];

// Stable slugs for the GA4 onboarding funnel (`onboarding_step.step_name`), one
// per wizard step. Kept separate from the display labels so renaming a label
// never splits the event's history.
const STEP_IDS = ['resume', 'domain', 'ssl', 'security'];

// Per-step heading shown above the step body (mirrors the CDN onboarding wizard).
const STEP_HEADINGS = [
  { title: 'Resume or Restart', description: 'Pick up an unfinished setup or start a fresh one.' },
  { title: 'Domain & Origin', description: 'Tell us the website domain and where its origin server lives.' },
  { title: 'SSL Certificate', description: 'Choose how the edge certificate is provisioned.' },
  { title: 'Security Features', description: 'Enable AI-WAF protection for this website.' },
];

// AI-WAF managed policies. Only Basic (1) is confirmed from the wire; the rest
// follow the portal dropdown order — confirm the ids against the vendor.
const WAF_POLICIES = [
  { value: 1, label: 'Basic (Recommended)' },
  { value: 2, label: 'Standard' },
  { value: 3, label: 'Strict' },
  { value: 4, label: 'Basic Plus' },
  { value: 5, label: 'Standard Plus' },
  { value: 6, label: 'Monitoring' },
  { value: 7, label: 'Balanced' },
];

const DEFAULT_CONFIG: QuickSetupConfig = {
  cdn: {
    name: '',
    urlPrefix: '',
    protocol: 'https://',
    origins: [],
    streamingService: false,
    redirectHttpToHttps: true,
    http2: false,
    active: true,
  },
  certificate: { type: 'auto', certificateId: '' },
  aiWaf: { enabled: true, policy: 1 },
  // The vendor's quick-setup create leaves these OFF; the user opts in on the
  // Security Features step. (Matches the flashcone console create payload; also
  // avoids minting an API-Protection app against an empty domain at create.)
  apiProtection: { enabled: false },
  botManagement: { enabled: false },
};

/**
 * Sanitize the config for the vendor round-trip. The vendor's quick-setup only
 * understands "auto"/"existing":
 *  - "free" (Tiger free-SSL) is a FE-only transient — until the Tiger cert is
 *    uploaded into the vendor pool and pinned as "existing" (with the numeric
 *    pool id), it maps to a vendor-safe "auto". A Tiger cert UUID must never
 *    reach `certificateId` (flashcone 500s on it).
 *  - "existing" sends the vendor pool cert id as a NUMBER (matches the flashcone
 *    console payload: `"certificateId": 122609`).
 */
function toVendorConfig(cfg: QuickSetupConfig): QuickSetupConfig {
  const t = cfg.certificate.type;
  const poolId = Number(cfg.certificate.certificateId);
  return {
    ...cfg,
    certificate: {
      type: t === 'free' ? 'auto' : t,
      certificateId: t === 'existing' && Number.isFinite(poolId) && poolId > 0 ? poolId : '',
    },
  };
}

/** A toggleable security-feature box (Security Features step). */
function FeatureBox({
  title,
  enabled,
  onToggle,
  children,
}: {
  title: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  children?: React.ReactNode;
}) {
  return (
    <div className={styles.featureBox}>
      <div className={styles.featureHead}>
        <span className={styles.bold}>{title}</span>
        <Switch checked={enabled} onCheckedChange={onToggle} />
      </div>
      {children}
    </div>
  );
}

// Cert statuses that mean "still resolving" — poll while the cert sits in one of
// these (mirrors the certinvent inventory page's TRANSIENT set).
const TRANSIENT: ReadonlySet<CertStatus> = new Set<CertStatus>([
  'awaiting_dns',
  'issuing',
  'pending',
]);

const CERT_STATUS_LABEL: Record<CertStatus, string> = {
  pending: 'Preparing…',
  awaiting_dns: 'Awaiting DNS',
  issuing: 'Issuing…',
  active: 'Active',
  failed: 'Failed',
  revoking: 'Revoking…',
  revoked: 'Revoked',
};

/** A single-line value + copy-to-clipboard button (used for the CNAME record). */
function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — ignore */
    }
  };
  return (
    <div className={styles.stack1}>
      <span className={styles.dnsLabel}>{label}</span>
      <div className={styles.copyRow}>
        <code className={styles.copyCode}>{value}</code>
        <IconButton variant="ghost" size="sm" aria-label={`Copy ${label}`} onClick={copy}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </IconButton>
      </div>
    </div>
  );
}

/**
 * Tiger free-SSL sub-flow (Step 3, "free" option): issue a cert for the wizard
 * domain via acme-dns delegation, show the one-time CNAME to add, poll
 * until the cert goes `active`, and hand the cert id back to the wizard.
 *
 * Scope is issue + verify only — attaching the cert to the vendor edge is a
 * separate step (task #17). The cert id is stored into
 * `config.certificate.certificateId` by the parent (via `onIssued`/saveStep).
 */
function TigerSslPanel({
  domain,
  certId,
  onIssued,
}: {
  domain: string;
  /** Persisted cert id (from config.certificate.certificateId), if already issued. */
  certId: string;
  /** Called with the freshly-issued cert id so the parent can persist it. */
  onIssued: (id: string) => void;
}) {
  const { show } = useToast();
  const issue = useIssueCert({
    onSuccess: (cert) => {
      onIssued(cert.id);
      show({ tone: 'success', title: 'Certificate requested', description: 'Add the DNS record below to finish.' });
    },
    onError: () => show({ tone: 'danger', title: 'Could not request certificate' }),
  });
  // Auto-provision the org's Tiger workspace (get-or-create, idempotent) before
  // issuing — so a user who never "subscribed" to Tiger doesn't hit a
  // "Tenant context required" 403; they're activated on first use.
  const activate = useActivateWorkspace();
  const issueCert = () => {
    if (!domain || issue.isPending || activate.isPending) return;
    activate.mutate(undefined, {
      onSuccess: () => issue.mutate({ dns_names: dnsNames }),
      onError: () =>
        show({ tone: 'danger', title: 'Could not set up your certificate workspace' }),
    });
  };
  const recheck = useRecheckCert({
    onSuccess: () =>
      show({ tone: 'info', title: 'Checking…', description: 'Verifying the DNS record and issuing shortly.' }),
    onError: () => show({ tone: 'danger', title: 'Recheck failed' }),
  });

  // Once issued, poll the detail while the cert is still resolving.
  const { data: cert, refetch } = useCertDetail(certId || undefined);
  const isTransient = !!cert && TRANSIENT.has(cert.status);
  useEffect(() => {
    if (!isTransient) return;
    const t = setInterval(() => refetch(), 8000);
    return () => clearInterval(t);
  }, [isTransient, refetch]);

  // Apex (example.com / example.com.vn) → also request `*.host` so subdomains are
  // covered. Leaf host (shop.example.com) → just the host; its wildcard would be
  // useless. Either way the acme-dns CNAME is at `_acme-challenge.<host>`, so this
  // doesn't change the single DNS record the customer adds.
  const dnsNames = useMemo(() => certDnsNamesFor(domain), [domain]);
  const coversWildcard = dnsNames.length > 1;

  // Not yet issued → show the request button.
  if (!certId) {
    return (
      <div className={styles.certBox}>
        <strong className={styles.strongDefault}>Issue a free SSL certificate</strong>
        <span>
          We'll request a managed certificate for <strong>{domain || 'your domain'}</strong>
          {coversWildcard && (
            <>
              {' '}
              and <strong>*.{domain}</strong>
            </>
          )}
          . You'll add one DNS record to prove control; it then renews automatically forever.
        </span>
        <div>
          <Button
            startIcon={<ShieldCheck size={15} />}
            disabled={!domain || issue.isPending || activate.isPending}
            onClick={issueCert}
          >
            {issue.isPending || activate.isPending ? 'Requesting…' : 'Issue free certificate'}
          </Button>
        </div>
      </div>
    );
  }

  const status = cert?.status;
  const del = cert?.delegation;

  return (
    <div className={styles.certBox}>
      <div className={styles.certStatusRow}>
        <strong className={styles.strongDefault}>Free SSL</strong>
        {status && (
          <Badge
            tone={status === 'active' ? 'success' : status === 'failed' ? 'danger' : 'info'}
            appearance="subtle"
            size="sm"
          >
            {CERT_STATUS_LABEL[status]}
          </Badge>
        )}
      </div>

      {status === 'active' ? (
        <Alert tone="success" title="Certificate active">
          Your certificate for {cert?.dns_names?.join(', ') || domain} is issued and will auto-renew. You can
          continue.
        </Alert>
      ) : status === 'failed' ? (
        <Alert tone="danger" title="Issuance failed">
          We couldn't issue the certificate. Check the DNS record is correct and try Recheck, or skip and verify
          later.
        </Alert>
      ) : del ? (
        <>
          <span>
            Add this <strong>one-time CNAME</strong> at your domain's DNS provider, then choose Recheck. We
            validate and issue automatically.
          </span>
          <div className={styles.cnameGrid}>
            <CopyField label="Host / Name" value={del.cname_host} />
            <CopyField label="Value / Target" value={del.cname_target} />
          </div>
        </>
      ) : (
        <span>Preparing the DNS delegation…</span>
      )}

      {status !== 'active' && (
        <div className={styles.certActionsRow}>
          <Button
            variant="secondary"
            startIcon={<RefreshCw size={15} />}
            disabled={recheck.isPending}
            onClick={() => recheck.mutate(certId)}
          >
            {recheck.isPending ? 'Checking…' : 'Recheck'}
          </Button>
          <span className={styles.hintText}>
            DNS can take a few minutes to propagate. This page also checks automatically.
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * WAAP Onboarding — the guided setup wizard: Resume/Restart → Domain & Origin →
 * SSL → Security Features → execute (provision). Backed by the private
 * /quick_setup session (create + per-step PUT + execute).
 *
 * Layout mirrors the CDN onboarding wizard: a single `Card` body holding a
 * @vnetwork/ui `Stepper`, a per-step heading, the step body, and a bottom
 * `ActionBar` (Back / primary).
 */
export function QuickSetupTab() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;
  const svc = serviceId ?? '';

  const [step, setStep] = useState(1);
  const [config, setConfig] = useState<QuickSetupConfig>(DEFAULT_CONFIG);
  const [record, setRecord] = useState<QuickSetupRecord | null>(null);
  const [deliveryDomain, setDeliveryDomain] = useState<string | null>(null);
  // Set when execute returns isSuccess:false (the vendor provisioned partially —
  // e.g. certificate config failed). Drives the error variant of the final step.
  const [executeError, setExecuteError] = useState<string | null>(null);
  // Resume/Restart pick ('resume' | 'new'); defaults to resume when a previous
  // session exists, else start-new.
  const [choice, setChoice] = useState<'resume' | 'new' | null>(null);
  // SSL step: Tiger free-SSL (managed inventory) vs a vendor-issued certificate —
  // kept SEPARATE from the vendor cert-type so "free SSL from Tiger" isn't a fake
  // vendor mode. Default Tiger (recommended).
  const [sslMode, setSslMode] = useState<'tiger' | 'vendor'>('tiger');
  // The issued Tiger cert id (FE-only). Kept out of config.certificate, which
  // stays purely vendor-facing (auto | existing+poolId). Once the Tiger cert is
  // active it's uploaded into the vendor pool and pinned as `existing`.
  const [tigerCertId, setTigerCertId] = useState('');

  const { data: sessions } = useQuickSetupSessions(svc);
  // Resume the MOST RECENT unfinished session (highest id), not the first one the
  // API happens to return. Starting a "new setup" mints a fresh session but the
  // API has no delete, so an abandoned earlier draft (e.g. a half-issued SSL for
  // a.com) still lingers in the list; picking the newest keeps the wizard on the
  // domain the user is actually onboarding instead of re-resuming the stale one.
  const previous =
    (sessions ?? [])
      .filter((s) => !s.executed_step)
      .reduce<QuickSetupRecord | null>((latest, s) => (!latest || s.id > latest.id ? s : latest), null) ??
    sessions?.[0] ??
    null;
  const effChoice: 'resume' | 'new' = choice ?? (previous ? 'resume' : 'new');

  // Funnel: log each wizard step the user reaches (1-based). Steps 1–4 only;
  // step 5 is the terminal success/error screen, tracked via complete() below.
  useEffect(() => {
    if (step < 1 || step > 4) return;
    trackOnboardingStep(step, STEP_IDS[step - 1]);
  }, [step]);

  // After provisioning, resolve the created website's real delivery CNAME
  // (<id>.cdn.vncdn.net) for the DNS guidance — it is NOT in the execute response
  // (that only carries the generic edge host). Find the domain in the service's
  // list, then read its website-info cname.
  const provisioned = step === 5 && !executeError;
  const { data: domainsData } = useServiceWaapDomainsPaged(svc, {
    page: 1,
    perPage: 100,
    enabled: provisioned,
  });
  const createdDomain = (domainsData?.data ?? []).find(
    (d) => d.domain === config.cdn.name.trim(),
  );
  const { data: websiteInfo } = useWaapAspect<WaapWebsiteInfo>(
    (createdDomain?.id as string | undefined) ?? '',
    WAAP_AREAS.websiteInfo,
  );
  const cnameTarget = createdDomain?.cname || websiteInfo?.cname || deliveryDomain || '';

  // #17.3 — best-effort attach of the wizard-issued Tiger cert to the freshly
  // provisioned WAAP domain. The execute flow does NOT return the created domain
  // id; it's resolved post-provision by matching the service's WAAP domain list
  // (`createdDomain`, above). Once that id is known and a free cert was issued,
  // bind it once. The provisioning itself never depends on this — a failure here
  // just leaves the cert in inventory to attach manually from the SSL tab.
  const createdDomainId = (createdDomain?.id as string | undefined) ?? '';
  const distributeTiger = useDistributeTigerCertToWaap(createdDomainId, {
    onError: (e) =>
      show({
        tone: 'warning',
        title: 'Certificate not attached',
        description: `${errMessage(e)} — you can attach it later from the website's SSL tab.`,
      }),
  });
  const distributedRef = useRef(false);
  useEffect(() => {
    if (distributedRef.current) return;
    if (!provisioned || !createdDomainId) return;
    // Fallback only: a Tiger cert was issued but NOT pre-uploaded into the vendor
    // pool (config.certificate isn't `existing`) — e.g. it wasn't active by
    // Execute time. Attach it now that the website exists. When it WAS pre-uploaded
    // (type `existing`), provisioning already bound it, so skip.
    if (sslMode !== 'tiger' || !tigerCertId) return;
    if (config.certificate.type === 'existing') return;
    distributedRef.current = true;
    distributeTiger.mutate({ cert_id: tigerCertId });
  }, [provisioned, createdDomainId, sslMode, tigerCertId, config.certificate.type, distributeTiger]);

  const onErr = (e: unknown) => show({ tone: 'danger', title: 'Onboarding failed', description: errMessage(e) });

  const create = useCreateQuickSetup(svc, { onError: onErr });
  const update = useUpdateQuickSetup(svc, { onError: onErr });
  const execute = useExecuteQuickSetup(svc, { onError: onErr });

  const busy = create.isPending || update.isPending || execute.isPending;
  const patchCdn = (p: Partial<QuickSetupConfig['cdn']>) =>
    setConfig((c) => ({ ...c, cdn: { ...c.cdn, ...p } }));

  // Live status of the Tiger cert (drives Step-3 Continue gating + the auto-upload).
  const { data: freeCert } = useCertDetail(tigerCertId || undefined);

  // Restore an in-flight Tiger cert on re-entry to the SSL step. The pending
  // cert's UUID lives only in `tigerCertId` state — it is never persisted (the
  // vendor round-trip strips it), so leaving Step 3 (Continue/Skip → Security
  // Features → Back) or reloading and resuming drops it, resetting the SSL panel
  // to the "issue" prompt and hiding the CNAME + verify status. The Tiger
  // inventory is the source of truth: look the domain's cert up and re-adopt the
  // still-resolving one so the panel keeps showing where the user left off.
  const wizardDomain = config.cdn.name.trim();
  const needRestoreCert =
    step === 3 && sslMode === 'tiger' && !tigerCertId && !!wizardDomain && !!record;
  const { data: domainCerts } = useCerts(
    { domain: wizardDomain },
    { enabled: needRestoreCert },
  );
  useEffect(() => {
    if (!needRestoreCert || !domainCerts?.length) return;
    const pending =
      domainCerts.find((c) => c.status === 'awaiting_dns' || c.status === 'issuing') ??
      domainCerts.find((c) => c.status === 'active');
    if (pending?.id) setTigerCertId(pending.id);
  }, [needRestoreCert, domainCerts]);

  // Once the Tiger cert is active, upload it into the vendor pool and pin it as
  // an `existing` cert (numeric vendor pool id) so Execute provisions with the
  // Tiger cert in ONE pass — no throwaway vendor auto cert, no separate attach.
  // Best-effort: on failure the config stays `free`→auto and the post-provision
  // distributeTiger fallback (above) attaches it after the website is created.
  const certinventUpload = useCertinventUpload(svc, {
    onError: (e) =>
      show({ tone: 'warning', title: 'Could not pre-upload certificate', description: errMessage(e) }),
  });
  const uploadedRef = useRef(false);
  useEffect(() => {
    if (uploadedRef.current) return;
    if (sslMode !== 'tiger' || !record || !tigerCertId) return;
    if (freeCert?.status !== 'active') return;
    uploadedRef.current = true;
    void (async () => {
      try {
        const poolId = await certinventUpload.mutateAsync({ certId: tigerCertId });
        const cfg: QuickSetupConfig = {
          ...config,
          certificate: { type: 'existing', certificateId: poolId },
        };
        setConfig(cfg);
        await update.mutateAsync({
          id: record.id,
          current_step: 3,
          configured_step: 3,
          config: toVendorConfig(cfg),
        });
      } catch {
        uploadedRef.current = false; // let the next poll retry
      }
    })();
    // config is intentionally omitted from deps — the ref guards single-run and
    // we read the latest via closure at fire time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sslMode, record, tigerCertId, freeCert?.status]);

  // Step 1 — Start a new session. Matches the vendor's own quick-setup flow: the
  // session is created here with the default config; an empty CDN block is fine
  // (the vendor UI creates with empty CDN too — the domain is filled on step 2).
  const start = async () => {
    const rec = await create.mutateAsync({
      solution_id: SOLUTION_ID,
      current_step: 1,
      configured_step: 1,
      config: toVendorConfig(DEFAULT_CONFIG),
    });
    if (!rec?.id) {
      show({ tone: 'danger', title: 'Onboarding failed', description: 'No session was created.' });
      return;
    }
    setRecord(rec);
    setConfig(rec.config ?? DEFAULT_CONFIG);
    setStep(2);
  };

  // Step 1 — Resume an unfinished session: load it and jump to where it left off.
  const resume = (s: QuickSetupRecord) => {
    setRecord(s);
    setConfig(s.config ?? DEFAULT_CONFIG);
    setStep(Math.min((s.configured_step || 0) + 1, 4));
  };

  // Start button on the Resume/Restart step.
  const onStart = () => {
    if (effChoice === 'resume' && previous) resume(previous);
    else void start();
  };

  // The Resume/Restart step is the wizard's first stop: the user picks whether to
  // resume an unfinished draft or start fresh, then clicks Start to advance. No
  // auto-submit — the choice is left to the user.

  // Persist the whole object for `stepNum`, then advance to `next`. The PUT may
  // respond with `null` (the portal doesn't echo the object on update), so keep
  // the config we just sent and the existing session id.
  const saveStep = async (stepNum: number, cfg: QuickSetupConfig, next: number) => {
    if (!record) return;
    const rec = await update.mutateAsync({
      id: record.id,
      current_step: stepNum,
      configured_step: stepNum,
      config: toVendorConfig(cfg),
    });
    // Keep the FE config as the source of truth — it holds the Tiger free-SSL cert
    // the vendor doesn't store (we send it a sanitized "auto"); only refresh the
    // record id/meta from the echo. Otherwise a vendor echo would clobber the cert.
    if (rec) setRecord({ ...rec, config: cfg });
    setConfig(cfg);
    setStep(next);
  };

  const continueDomain = () => {
    if (!config.cdn.name.trim() || !config.cdn.urlPrefix.trim()) {
      show({ tone: 'danger', title: 'Website domain and origin server are required' });
      return;
    }
    const cfg: QuickSetupConfig = {
      ...config,
      cdn: { ...config.cdn, origins: [{ urlPrefix: config.cdn.protocol + config.cdn.urlPrefix }] },
    };
    void saveStep(2, cfg, 3);
  };

  const continueSsl = () => {
    // Tiger option: require an issued + active cert before advancing. If it's
    // still resolving, nudge the user toward "Skip & verify later" (which provisions
    // with a vendor auto cert and attaches the Tiger cert once it goes active).
    if (sslMode === 'tiger') {
      if (!tigerCertId) {
        show({ tone: 'danger', title: 'Issue a certificate first, or choose the vendor option' });
        return;
      }
      if (freeCert?.status !== 'active') {
        show({
          tone: 'warning',
          title: 'Certificate not active yet',
          description: 'Wait for it to go active, or choose “Skip & verify later”.',
        });
        return;
      }
    }
    void saveStep(3, config, 4);
  };

  // "Skip & verify later" — don't block on the Tiger cert going active. Provision
  // with the vendor auto (free) certificate now and continue to Security Features;
  // the config's "free" cert maps to a vendor-safe "auto", and any issued Tiger
  // cert still uploads + attaches in the background once it goes active, so DNS
  // verification can be finished later without stalling the wizard.
  const skipSsl = () => {
    show({
      tone: 'info',
      title: 'Continuing with a free certificate',
      description: tigerCertId
        ? 'We’ll attach your managed certificate automatically once it’s verified.'
        : 'The edge issues a free certificate; you can change SSL later from the console.',
    });
    void saveStep(3, config, 4);
  };

  const complete = async () => {
    if (!record) return;
    await update.mutateAsync({
      id: record.id,
      current_step: 4,
      configured_step: 4,
      config: toVendorConfig(config),
    });
    const domain = config.cdn.name.trim();
    const res = await execute.mutateAsync({ setupId: record.id, domain });
    setDeliveryDomain(res?.deliveryDomain ?? null);
    // The vendor returns HTTP 200 even when a provisioning step fails; the real
    // outcome is in isSuccess. Don't show "Setup Complete!" on a failure.
    const failure =
      res && res.isSuccess === false
        ? res.message || res.detailMsg || 'Setup failed. Please try again.'
        : null;
    setExecuteError(failure);
    // Funnel outcome: the website is only really provisioned once execute
    // succeeds (the domain record is created at execute, not on the domain step).
    if (failure) {
      trackOnboardingFailed(4, failure);
    } else {
      trackDomainAdd('waap', domain);
      if (config.aiWaf.enabled) trackWafEnable();
      trackOnboardingComplete(domain || undefined);
    }
    setStep(5);
  };

  const header = (
    <PageHeader
      breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'Onboarding' }]} />}
      title="Onboarding"
    />
  );

  const heading = STEP_HEADINGS[step - 1];
  const stepperIndex = Math.min(step, STEP_LABELS.length) - 1;

  return (
    <Container size="md">
      {header}
      <Card>
        <CardBody>
          <div className={styles.wizard}>
            <Stepper steps={STEP_LABELS} current={stepperIndex} reached={stepperIndex} />

            {step <= 4 && heading && (
              <div className={styles.stepHead}>
                <h2 className={styles.stepTitle}>{heading.title}</h2>
                <p className={styles.stepDesc}>{heading.description}</p>
              </div>
            )}

            <div key={step} className={styles.stepBody}>
              {step === 1 ? (
                <RadioGroup
                  value={effChoice}
                  onValueChange={(v) => {
                    if (v === 'resume' && !previous) return;
                    setChoice(v as 'resume' | 'new');
                  }}
                >
                  <div className={styles.optionGroup}>
                    <label
                      className={`${styles.optionCard}${effChoice === 'resume' ? ` ${styles.optionCardSelected}` : ''}${!previous ? ` ${styles.optionCardDisabled}` : ''}`}
                    >
                      <RadioGroupItem value="resume" disabled={!previous} className={styles.optionRadio} />
                      <div>
                        <div className={styles.optionTitle}>Resume previous setup</div>
                        <div className={styles.optionDesc}>
                          Continue from where you left off. Domain:{' '}
                          {previous?.config?.cdn?.name || 'Not configured'}
                        </div>
                      </div>
                    </label>
                    <label
                      className={`${styles.optionCard}${effChoice === 'new' ? ` ${styles.optionCardSelected}` : ''}`}
                    >
                      <RadioGroupItem value="new" className={styles.optionRadio} />
                      <div>
                        <div className={styles.optionTitle}>Start new setup</div>
                        <div className={styles.optionDesc}>
                          Start a fresh setup. Any unfinished setup will be cleared.
                        </div>
                      </div>
                    </label>
                  </div>
                </RadioGroup>
              ) : step === 2 ? (
                <div className={styles.formCol}>
                  <Labeled
                    label="Website Domain"
                    hint="Enter the public address of your website (e.g., example.com) that visitors will use. Do not include http:// or https://."
                  >
                    <Input
                      placeholder="example.com"
                      value={config.cdn.name}
                      onChange={(e) => patchCdn({ name: e.target.value })}
                    />
                  </Labeled>
                  <Labeled label="Origin Server">
                    <div className={styles.row2}>
                      <Select value={config.cdn.protocol} onValueChange={(v) => patchCdn({ protocol: v })}>
                        <SelectTrigger className={styles.select120}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="https://">https://</SelectItem>
                          <SelectItem value="http://">http://</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Enter your origin server IP or domain, IPv6 is not supported."
                        value={config.cdn.urlPrefix}
                        onChange={(e) => patchCdn({ urlPrefix: e.target.value })}
                        className={styles.flex1}
                      />
                    </div>
                  </Labeled>
                  <span className={styles.hintText}>
                    The above information can be changed later under Websites if needed.
                  </span>
                </div>
              ) : step === 3 ? (
                <div className={styles.formCol}>
                  <Labeled
                    label="How do you want to secure this site?"
                    hint="You can change or add certificates later from the website's SSL tab."
                  >
                    <RadioGroup
                      value={sslMode}
                      onValueChange={(v) => {
                        const mode = v as 'tiger' | 'vendor';
                        setSslMode(mode);
                        // Reset per-mode: Tiger clears the vendor cert (re-issued via
                        // the panel); vendor pins `auto` and drops any Tiger cert.
                        uploadedRef.current = false;
                        distributedRef.current = false;
                        if (mode === 'vendor') setTigerCertId('');
                        setConfig((c) => ({
                          ...c,
                          certificate: { type: 'auto', certificateId: '' },
                        }));
                      }}
                    >
                      <label className={styles.radioRow}>
                        <RadioGroupItem value="tiger" />
                        <span>
                          <strong>Free managed SSL (Tiger)</strong> — issued for you, auto-renews, and is
                          attachable across your services. Recommended.
                        </span>
                      </label>
                      <label className={styles.radioRow}>
                        <RadioGroupItem value="vendor" />
                        <span>
                          <strong>Auto-generated vendor certificate</strong> — the edge issues a Let's Encrypt
                          certificate after you point a CNAME.
                        </span>
                      </label>
                    </RadioGroup>
                  </Labeled>

                  {sslMode === 'tiger' ? (
                    <TigerSslPanel
                      domain={config.cdn.name.trim()}
                      certId={tigerCertId}
                      onIssued={setTigerCertId}
                    />
                  ) : (
                    <div className={styles.certBox}>
                      <strong className={styles.strongDefault}>
                        Auto-generated certificate — update the DNS records first.
                      </strong>
                      <span>
                        Add a CNAME record pointing <strong>{config.cdn.name || 'your domain'}</strong> to the delivery
                        domain we provide. DNS changes may take up to 1 hour to propagate globally.
                      </span>
                    </div>
                  )}
                </div>
              ) : step === 4 ? (
                <div className={styles.featureGrid}>
                  <FeatureBox
                    title="AI-WAF Protection"
                    enabled={config.aiWaf.enabled}
                    onToggle={(v) => setConfig((c) => ({ ...c, aiWaf: { ...c.aiWaf, enabled: v } }))}
                  >
                    {config.aiWaf.enabled && (
                      <>
                        <Select
                          value={String(config.aiWaf.policy)}
                          onValueChange={(v) => setConfig((c) => ({ ...c, aiWaf: { ...c.aiWaf, policy: Number(v) } }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {WAF_POLICIES.map((p) => (
                              <SelectItem key={p.value} value={String(p.value)}>
                                {p.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {/* Explain the selected policy inline (source of truth:
                            the shared WAF_POLICIES catalog) so customers see what
                            each managed policy does without leaving onboarding. */}
                        {wafPolicyById(config.aiWaf.policy)?.description && (
                          <p className={styles.policyHint}>
                            {wafPolicyById(config.aiWaf.policy)?.description}
                          </p>
                        )}
                      </>
                    )}
                  </FeatureBox>
                </div>
              ) : executeError ? (
                <div className={styles.successBox}>
                  <XCircle size={48} className={styles.errorIcon} />
                  <div className={styles.successTitle}>Setup Error!</div>
                  <div className={styles.muted}>{executeError}</div>
                  <Button
                    onClick={() => {
                      setExecuteError(null);
                      setStep(4);
                    }}
                  >
                    Fix the current Setup
                  </Button>
                </div>
              ) : (
                <div className={styles.successBox}>
                  <CheckCircle2 size={48} className={styles.successIcon} />
                  <div className={styles.successTitle}>Setup Complete!</div>
                  <div className={styles.muted}>
                    Your website is protected. Point your domain's DNS to {brand.shortName} to
                    activate delivery.
                  </div>
                  <div className={styles.dnsGuide}>
                    <div className={styles.dnsGuideHead}>What is a CNAME?</div>
                    <p className={styles.dnsLabel}>
                      A CNAME (Canonical Name) record redirects traffic from your domain to
                      our service. This ensures all requests pass through our protection layer
                      before reaching your origin server.
                    </p>

                    <div className={styles.dnsGuideHead}>How to set up:</div>
                    <ol className={styles.dnsSteps}>
                      <li>Log in to your DNS provider (e.g., GoDaddy, Cloudflare, AWS Route 53).</li>
                      <li>Find the DNS management section.</li>
                      <li>Add a new CNAME record with the following information:</li>
                    </ol>

                    <div className={styles.dnsRecord}>
                      <div className={styles.dnsRecordRow}>
                        <span className={styles.dnsRecordKey}>Type</span>
                        <span className={styles.dnsRecordVal}>CNAME</span>
                      </div>
                      <div className={styles.dnsRecordRow}>
                        <span className={styles.dnsRecordKey}>Name</span>
                        <span className={styles.dnsRecordVal}>{config.cdn.name || 'your-domain.com'}</span>
                      </div>
                      <div className={styles.dnsRecordRow}>
                        <span className={styles.dnsRecordKey}>Value</span>
                        <span className={styles.copyRow}>
                          <code className={styles.copyCode}>{cnameTarget || '<id>.cdn.vncdn.net'}</code>
                          <IconButton
                            variant="ghost"
                            size="sm"
                            aria-label="Copy CNAME value"
                            onClick={() => {
                              void navigator.clipboard.writeText(cnameTarget);
                              show({ tone: 'success', title: 'Copied' });
                            }}
                          >
                            <Copy size={14} />
                          </IconButton>
                        </span>
                      </div>
                    </div>

                    <span className={styles.dnsLabel}>
                      Note: DNS changes may take up to 1 hour to propagate globally.
                    </span>
                  </div>
                  <Button onClick={() => navigate(`${base}/websites`)}>Go to Websites</Button>
                </div>
              )}
            </div>

            {step <= 4 && (
              <ActionBar
                start={
                  step > 1 ? (
                    <Button variant="ghost" onClick={() => setStep((s) => s - 1)} disabled={busy}>
                      Back
                    </Button>
                  ) : undefined
                }
                end={
                  step === 1 ? (
                    <Button onClick={onStart} disabled={create.isPending}>
                      {create.isPending ? 'Starting…' : 'Start'}
                    </Button>
                  ) : step === 2 ? (
                    <Button onClick={continueDomain} disabled={update.isPending}>
                      {update.isPending ? 'Saving…' : 'Continue'}
                    </Button>
                  ) : step === 3 ? (
                    <div className={styles.actionEndRow}>
                      <Button variant="ghost" onClick={() => void skipSsl()} disabled={update.isPending}>
                        Skip &amp; verify later
                      </Button>
                      <Button onClick={continueSsl} disabled={update.isPending}>
                        {update.isPending ? 'Saving…' : 'Continue'}
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={complete} disabled={busy}>
                      {execute.isPending ? 'Provisioning…' : 'Complete Setup'}
                    </Button>
                  )
                }
              />
            )}
          </div>
        </CardBody>
      </Card>
    </Container>
  );
}
